import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ShoppingBag, Lock, Sparkles, PawPrint } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { ShareMenu } from "@/components/ShareMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { API } from "@/lib/api";
import { getShopStatus, getPreviewToken, setPreviewToken, AVAIL_STYLE, formatPriceRange, formatOrigRange, leadText, variantPrice, variantOriginalPrice, ColorCountBadge } from "@/lib/shop";

// Price with optional crossed-out original + "Sale" badge (shared by catalog, product page, wishlist)
export const PriceTag = ({ p, variant = null, big = false }) => {
  const { t } = useLang();
  const onSale = variant ? variant.sale_price != null : !!p.on_sale;
  const effTxt = variant ? `€${variantPrice(p, variant).toFixed(2)}` : formatPriceRange(p);
  const origTxt = variant ? `€${variantOriginalPrice(p, variant).toFixed(2)}` : formatOrigRange(p);
  return (
    <span className="inline-flex items-baseline gap-2 flex-wrap">
      {onSale && (
        <span className={`line-through decoration-rose-400/70 text-brand-ink/40 ${big ? "text-lg" : "text-sm"}`} data-testid="price-original">{origTxt}</span>
      )}
      <span
        className={`font-heading font-bold ${onSale ? "text-rose-600" : "text-brand"} ${big ? "text-3xl" : "text-xl"}`}
        data-testid={big ? "product-price" : undefined}
      >
        {effTxt}
      </span>
      {onSale && (
        <span className="rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold px-2 py-0.5 uppercase tracking-wide" data-testid="sale-badge">{t.shop.sale_badge}</span>
      )}
    </span>
  );
};

export const productName = (p, lang) => (lang === "en" && p.name_en ? p.name_en : p.name_gr);
export const productDesc = (p, lang) => (lang === "en" && p.description_en ? p.description_en : p.description_gr);
export const productImg = (p, i = 0) => (p.images?.[i] ? `${API}/${p.images[i]}` : null);

export const AvailabilityBadge = ({ availability, lead, className = "" }) => {
  const { t, lang } = useLang();
  const s = t.shop;
  const label = s[`avail_${availability}`] || availability;
  const leadTxt = availability === "on_order" && lead ? leadText(lang, lead) : null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${AVAIL_STYLE[availability] || ""} ${className}`} data-testid={`avail-${availability}`}>
      {label}
      {leadTxt && <span className="font-semibold opacity-80">· {leadTxt}</span>}
    </span>
  );
};

const ComingSoon = () => {
  const { t } = useLang();
  const s = t.shop;
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const unlock = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.post("/shop/unlock", { password: pw });
      setPreviewToken(res.data.preview_token);
      window.dispatchEvent(new Event("cfm-shop-unlock"));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-soft/30">
      <Navbar />
      <main className="pt-32 pb-24 px-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-lg mx-auto text-center bg-white rounded-[2rem] border border-brand/10 shadow-[0_20px_60px_rgba(134,77,99,0.12)] p-10"
          data-testid="shop-coming-soon"
        >
          <span className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-brand-soft text-brand mb-6">
            <Sparkles className="h-9 w-9" />
          </span>
          <h1 className="font-heading text-4xl font-bold text-brand-ink">{s.coming_soon_title}</h1>
          <p className="mt-4 text-brand-muted leading-relaxed">{s.coming_soon_body}</p>
          <div className="mt-8 pt-6 border-t border-brand/10">
            <div className="flex gap-2 max-w-xs mx-auto">
              <Input
                type="password"
                value={pw}
                onChange={(e) => { setPw(e.target.value); setError(false); }}
                onKeyDown={(e) => e.key === "Enter" && unlock()}
                placeholder={s.unlock_ph}
                className={`rounded-full border-brand/20 bg-brand-cream h-11 ${error ? "border-rose-400" : ""}`}
                data-testid="shop-unlock-input"
              />
              <Button onClick={unlock} disabled={loading || !pw} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11 px-5" data-testid="shop-unlock-btn">
                <Lock className="h-4 w-4" />
              </Button>
            </div>
            {error && <p className="mt-2 text-sm text-rose-600" data-testid="shop-unlock-error">{s.unlock_error}</p>}
          </div>
          <Button variant="ghost" onClick={() => navigate("/")} className="mt-6 rounded-full text-brand font-bold" data-testid="shop-back-home">
            Care For Me
          </Button>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default function ShopPage() {
  const { t, lang } = useLang();
  const s = t.shop;
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandCards, setBrandCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [brand, setBrand] = useState("");
  const [sort, setSort] = useState("newest");
  const [hubView, setHubView] = useState(true);

  useEffect(() => {
    // spread → fresh object ref so React re-renders even when the cached status is unchanged
    const check = () => getShopStatus().then((st) => setStatus({ ...st }));
    check();
    window.addEventListener("cfm-shop-unlock", check);
    return () => window.removeEventListener("cfm-shop-unlock", check);
  }, []);

  const allowed = status && (status.enabled || getPreviewToken());

  useEffect(() => {
    api.get("/shop/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/shop/brands").then((r) => setBrandCards(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!allowed) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (cat) params.set("category", cat);
    if (brand) params.set("brand", brand);
    if (sort) params.set("sort", sort);
    api
      .get(`/shop/products?${params.toString()}`)
      .then((r) => {
        setProducts(r.data.products);
        setBrands(r.data.brands);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [allowed, q, cat, brand, sort]);

  const catName = useMemo(() => {
    const map = {};
    categories.forEach((c) => (map[c.id] = lang === "en" && c.name_en ? c.name_en : c.name_gr));
    return map;
  }, [categories, lang]);

  if (status === null) return null;
  if (!allowed) return <ComingSoon />;

  const searching = !!q.trim() || !!cat || !!brand;
  const showGrid = !hubView || searching;
  const brandCardsVisible = brandCards.filter((b) => b.count > 0);
  const itemCount = (n) => (lang === "el" ? `${n} ${n === 1 ? "προϊόν" : "προϊόντα"}` : `${n} ${n === 1 ? "product" : "products"}`);
  const openBrand = (b) => { setQ(""); setCat(""); setBrand(b); setHubView(false); };
  const openCat = (c) => { setQ(""); setBrand(""); setCat(c); setHubView(false); };
  const openAll = () => { setQ(""); setCat(""); setBrand(""); setHubView(false); };
  const backToHub = () => { setQ(""); setCat(""); setBrand(""); setHubView(true); };

  const HubCard = ({ image, title, count, onClick, testid, tall }) => (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45 }}
      onClick={onClick}
      className={`group relative overflow-hidden rounded-3xl border border-brand/10 text-left shadow-[0_10px_34px_rgba(134,77,99,0.10)] hover:shadow-[0_20px_50px_rgba(134,77,99,0.18)] hover:-translate-y-1 transition-[box-shadow,transform] duration-300 no-a11y-underline ${tall ? "aspect-[4/5]" : "aspect-[4/3]"}`}
      data-testid={testid}
    >
      {image ? (
        <img src={`${API}/${image}`} alt={title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-brand-soft via-brand-cream to-stone-alt grid place-items-center text-brand/25">
          <PawPrint className="h-16 w-16" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-brand-ink/10 to-transparent" />
      {count != null && (
        <span className="absolute top-3 right-3 rounded-full bg-white/90 backdrop-blur-sm text-brand-ink text-[11px] font-bold px-2.5 py-1 shadow-sm">{itemCount(count)}</span>
      )}
      <span className="absolute bottom-4 left-4 right-4 font-heading text-2xl font-bold text-white drop-shadow-sm">{title}</span>
    </motion.button>
  );

  return (
    <div className="min-h-screen bg-brand-soft/30">
      <Seo title={t.seo.shop_title} description={t.seo.shop_desc} path="/shop" />
      <Navbar />
      <main className="pt-28 pb-24 px-5 sm:px-8 max-w-7xl mx-auto" data-testid="shop-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-soft text-brand px-4 py-1.5 text-xs font-bold uppercase tracking-wide">
            <ShoppingBag className="h-3.5 w-3.5" /> Boutique
          </span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold text-brand-ink">{s.title}</h1>
          <p className="mt-3 text-brand-muted">{s.subtitle}</p>
          <p className="mt-1 text-xs text-brand-ink/50">{s.vat_note}</p>
        </motion.div>

        {/* Search — always on top; typing jumps straight to results */}
        <div className="mt-10" data-testid="shop-filters">
          <div className={`relative ${showGrid ? "" : "max-w-xl mx-auto"}`}>
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink/40" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={s.search_ph}
              className="pl-11 rounded-full border-brand/20 bg-white h-12 focus-visible:ring-brand"
              data-testid="shop-search"
            />
          </div>
          {showGrid && (
            <div className="mt-3 flex flex-col md:flex-row gap-3">
              <Select value={cat || "all"} onValueChange={(v) => { setCat(v === "all" ? "" : v); }}>
                <SelectTrigger className="rounded-full border-brand/20 bg-white h-12 md:w-52" data-testid="shop-filter-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{s.all_categories}</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{lang === "en" && c.name_en ? c.name_en : c.name_gr}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={brand || "all"} onValueChange={(v) => setBrand(v === "all" ? "" : v)}>
                <SelectTrigger className="rounded-full border-brand/20 bg-white h-12 md:w-48" data-testid="shop-filter-brand">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{s.all_brands}</SelectItem>
                  {brands.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="rounded-full border-brand/20 bg-white h-12 md:w-48" data-testid="shop-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{s.sort_newest}</SelectItem>
                  <SelectItem value="price_asc">{s.sort_price_asc}</SelectItem>
                  <SelectItem value="price_desc">{s.sort_price_desc}</SelectItem>
                  <SelectItem value="name">{s.sort_name}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Hub — category cards first, then partner cards, then "all products" */}
        {!showGrid && (
          <div className="mt-12 space-y-14" data-testid="shop-hub">
            {categories.length > 0 && (
              <section>
                <h2 className="font-heading text-3xl font-bold text-brand-ink mb-5 text-left">{s.hub_categories}</h2>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {categories.map((c) => (
                    <HubCard key={c.id} tall image={c.image} title={lang === "en" && c.name_en ? c.name_en : c.name_gr} count={c.count} onClick={() => openCat(c.id)} testid={`cat-card-${c.id}`} />
                  ))}
                </div>
              </section>
            )}
            {brandCardsVisible.length > 0 && (
              <section>
                <h2 className="font-heading text-3xl font-bold text-brand-ink mb-5 text-left">{s.hub_brands}</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                  {brandCardsVisible.map((b) => (
                    <HubCard key={b.name} image={b.image} title={b.name} count={b.count} onClick={() => openBrand(b.name)} testid={`brand-card-${b.name}`} />
                  ))}
                </div>
              </section>
            )}
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
              onClick={openAll}
              className="w-full rounded-3xl bg-brand text-brand-cream px-8 py-8 sm:py-10 flex items-center justify-between gap-4 shadow-[0_16px_44px_rgba(134,77,99,0.25)] hover:bg-brand-hover transition-colors no-a11y-underline"
              data-testid="shop-all-card"
            >
              <span className="text-left">
                <span className="block font-heading text-2xl sm:text-3xl font-bold">{s.hub_all}</span>
                <span className="block mt-1 text-sm text-brand-cream/80">{s.hub_all_hint}</span>
              </span>
              <Sparkles className="h-8 w-8 shrink-0" />
            </motion.button>
          </div>
        )}

        {/* Back to hub */}
        {showGrid && !hubView && (
          <button onClick={backToHub} className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand hover:text-brand-hover transition-colors no-a11y-underline" data-testid="shop-back-hub">
            ← {s.hub_back}
          </button>
        )}

        {/* Grid */}
        {!showGrid ? null : loading ? (
          <p className="text-center text-brand-ink/40 py-20">…</p>
        ) : products.length === 0 ? (
          <div className="text-center py-20" data-testid="shop-empty">
            <PawPrint className="h-10 w-10 mx-auto text-brand/30 mb-3" />
            <p className="text-brand-ink/50">{s.empty}</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="shop-grid">
            {products.map((p, i) => (
              <motion.div
                key={p.id}
                role="button"
                tabIndex={0}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}
                onClick={() => navigate(`/shop/product/${p.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter") navigate(`/shop/product/${p.id}`); }}
                className="group cursor-pointer text-left bg-white rounded-3xl border border-brand/10 overflow-hidden shadow-[0_8px_30px_rgba(134,77,99,0.06)] hover:shadow-[0_16px_44px_rgba(134,77,99,0.14)] hover:-translate-y-1 transition-[box-shadow,transform] duration-300 no-a11y-underline"
                data-testid={`product-card-${p.id}`}
              >
                <div className="relative aspect-square bg-stone-alt/50 overflow-hidden">
                  {productImg(p) ? (
                    <img src={productImg(p)} alt={productName(p, lang)} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  ) : (
                    <div className="h-full w-full grid place-items-center text-brand/25">
                      <PawPrint className="h-14 w-14" />
                    </div>
                  )}
                  {status?.colors_badge && <ColorCountBadge count={p.color_count} lang={lang} />}
                  <span className="absolute top-2.5 left-2.5 z-10 opacity-90 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    <ShareMenu
                      url={`${API}/share/product/${p.id}`}
                      title={productName(p, lang)}
                      iconOnly
                      testid={`card-share-${p.id}`}
                      triggerClassName="grid place-items-center h-9 w-9 rounded-full bg-white/90 backdrop-blur-sm border border-brand/15 text-brand shadow-sm hover:bg-white"
                    />
                  </span>
                </div>
                <div className="p-5">
                  {p.brand && <p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/40">{p.brand}</p>}
                  <h3 className="font-heading text-xl font-bold text-brand-ink leading-snug line-clamp-2 min-h-[3.4rem]">{productName(p, lang)}</h3>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-xs text-brand-ink/50 truncate">{p.category_id && catName[p.category_id] ? catName[p.category_id] : ""}</span>
                    <div className="text-right whitespace-nowrap"><PriceTag p={p} /></div>
                  </div>
                  <div className="mt-3">
                    <AvailabilityBadge availability={p.availability} lead={p.lead_days} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
